/**
 * Cart Price Cache Utility (Shopify-style)
 * 
 * Stores validated cart data with TTL for better UX
 * - Prices are cached for 5 minutes
 * - Always validated before checkout
 * - Backend is the source of truth
 */

export interface CachedCartData {
  items: any[];
  subtotal: number;
  itemCount: number;
  shippingCost?: number;
  total?: number;
  isFreeShipping?: boolean;
  shippingMessage?: string;
  cachedAt: number;
  version?: number;
}

const CACHE_KEY = 'cart-price-cache';
const CACHE_TTL = 5 * 60 * 1000;
const CACHE_VERSION = 1;

export const cartCache = {
  /**
   * Clear cache
   */
  clear(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('🗑️ Cart cache cleared');
    } catch (error) {
      console.error('Error clearing cart cache:', error);
    }
  },

  /**
   * Identifies the cart consistently across platforms and saves/reads
   */
  _getStableHash(items: any[]): string {
    if (!items || items.length === 0) return 'empty';
    
    // Create a stable representation of each item
    const representations = items.map(i => {
      const productId = i.productId || '';
      const variantId = i.variantId || '';
      const quantity = i.quantity || 0;
      const attributes = i.attributes ? JSON.stringify(Object.fromEntries(
        Object.entries(i.attributes).sort(([a], [b]) => a.localeCompare(b))
      )) : '';
      
      return `${productId}|${variantId}|${quantity}|${attributes}`;
    });

    // Sort the individual item strings to make the overall array order irrelevant
    return JSON.stringify(representations.sort());
  },

  /**
   * Save cart data to cache
   */
  set(currentItems: any[], data: Omit<CachedCartData, 'cachedAt'>): void {
    try {
      const itemsHash = this._getStableHash(currentItems);

      const cached: CachedCartData & { itemsHash: string } = {
        ...data,
        itemsHash,
        cachedAt: Date.now(),
        version: CACHE_VERSION,
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
      console.log('💾 Cart cache saved (hash:', itemsHash.substring(0, 20) + '...)');
    } catch (error) {
      console.error('Error saving cart cache:', error);
    }
  },

  /**
   * Get cached cart data if not expired and items match
   */
  get(currentItems: any[]): CachedCartData | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);
      if (!data.version || data.version !== CACHE_VERSION) {
        this.clear();
        return null;
      }

      // Validar si los items actuales coinciden con lo cacheado
      const currentHash = this._getStableHash(currentItems);
      if (data.itemsHash !== currentHash) {
        console.log('🔄 Cart items changed or order mismatch, cache invalid');
        return null;
      }

      const age = Date.now() - data.cachedAt;
      if (age > CACHE_TTL) {
        this.clear();
        return null;
      }

      return data as CachedCartData;
    } catch (error) {
      this.clear();
      return null;
    }
  },

  /**
   * Get cache age in seconds
   */
  getAge(): number | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data: CachedCartData = JSON.parse(cached);
      return Math.round((Date.now() - data.cachedAt) / 1000);
    } catch {
      return null;
    }
  }
};
